import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import type { OutlineItem } from '../lib/doc-structure.ts'

export type RelatedDoc = { id: string; title: string; to: string }

/**
 * Tracks which section the reader is currently in.
 *
 * The bottom margin collapses the observer's viewport to a band across the top
 * of the screen, so a heading counts as "current" from the moment it reaches
 * that band until the next one does — rather than the whole time it happens to
 * be visible, which would light up three sections at once on a long procedure.
 */
function useActiveSection(ids: string[]): string | null {
  const [active, setActive] = useState<string | null>(null)

  useEffect(() => {
    // jsdom has no IntersectionObserver, and neither do older browsers; the
    // rail stays a plain list of links, which is its job with or without this.
    if (typeof IntersectionObserver === 'undefined') return
    const headings = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null)
    if (headings.length === 0) return

    const visible = new Set<string>()
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.add(entry.target.id)
          else visible.delete(entry.target.id)
        }
        // Document order, so the topmost heading in the band wins.
        const current = ids.find((id) => visible.has(id))
        if (current) setActive(current)
      },
      { rootMargin: '-80px 0px -70% 0px' },
    )
    for (const el of headings) observer.observe(el)
    return () => observer.disconnect()
  }, [ids])

  return active
}

/**
 * The rail beside a document: where you are in it, and where it points next.
 *
 * "Related documents" is the interactive half. The source procedures name their
 * companions in prose ("using the QA 2 Device set up and authorisation tool")
 * with no way to reach them; these are the same references as real links.
 */
export function DocRail({
  outline, related = [],
}: {
  outline: OutlineItem[]
  related?: RelatedDoc[]
}) {
  const active = useActiveSection(outline.map((o) => o.id))

  if (outline.length === 0 && related.length === 0) return null

  return (
    <aside className="doc-rail">
      {outline.length > 0 && (
        <nav aria-labelledby="doc-outline-label">
          <p className="rail__label" id="doc-outline-label">On this page</p>
          <div className="toc">
            {outline.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                className={[
                  'toc__link',
                  item.level === 3 ? 'toc__link--sub' : '',
                  active === item.id ? 'is-current' : '',
                ].filter(Boolean).join(' ')}
                aria-current={active === item.id ? 'true' : undefined}
              >
                {item.text}
              </a>
            ))}
          </div>
        </nav>
      )}

      {related.length > 0 && (
        <nav aria-labelledby="doc-related-label">
          <p className="rail__label" id="doc-related-label">Related documents</p>
          <div className="related">
            {related.map((doc) => (
              <Link key={doc.id} to={doc.to} className="related__link">
                <span aria-hidden="true">→</span>
                {doc.title}
              </Link>
            ))}
          </div>
        </nav>
      )}
    </aside>
  )
}
